/**
 * Datadog Browser RUM + Logs — initialized when VITE_DD_APPLICATION_ID and
 * VITE_DD_CLIENT_TOKEN are set at build time. See docs/DATADOG_SETUP.md.
 */

import { datadogLogs } from '@datadog/browser-logs';
import { datadogRum } from '@datadog/browser-rum';
import { reactPlugin } from '@datadog/browser-rum-react';

import type { ErrorBoundaryReportContext } from './boundaryErrorContext';

let initialized = false;

/**
 * Auth bootstrap calls that return 401 for anonymous / expired sessions.
 * Synthetics and cold page loads hit these before login; counting them as RUM
 * errors drives a false ~100% error rate on docufy-web.
 */
const EXPECTED_AUTH_401_URL =
  /\/api\/v1\/(?:auth\/(?:me|refresh)|users\/profile|organizations\/me)(?:\?|$)/i;

function isExpectedAuth401(url: string | undefined, statusCode: number | undefined): boolean {
  return statusCode === 401 && typeof url === 'string' && EXPECTED_AUTH_401_URL.test(url);
}

/** Drop expected unauthenticated auth probes from RUM (resources + network errors). */
function shouldSendRumEvent(event: { type: string; [key: string]: unknown }): boolean {
  if (event.type === 'resource') {
    const resource = event.resource as { url?: string; status_code?: number } | undefined;
    if (isExpectedAuth401(resource?.url, resource?.status_code)) {
      return false;
    }
  }
  if (event.type === 'error') {
    const error = event.error as
      | { message?: string; resource?: { url?: string; status_code?: number } }
      | undefined;
    const url = error?.resource?.url ?? error?.message;
    const status =
      error?.resource?.status_code ?? (/\b401\b/.test(error?.message ?? '') ? 401 : undefined);
    if (isExpectedAuth401(url, status)) {
      return false;
    }
  }
  return true;
}

function ddSite(): string {
  // Org is US5 — keep in sync with ECS DD_SITE / docs/DATADOG_SETUP.md
  return String(import.meta.env.VITE_DD_SITE || 'us5.datadoghq.com');
}

function ddEnv(): string {
  return String(import.meta.env.VITE_ENV ?? import.meta.env.MODE ?? 'development');
}

export function initDatadog(): void {
  const applicationId = import.meta.env.VITE_DD_APPLICATION_ID as string | undefined;
  const clientToken = import.meta.env.VITE_DD_CLIENT_TOKEN as string | undefined;
  if (!applicationId || !clientToken || initialized) {
    return;
  }

  const service = String(import.meta.env.VITE_DD_SERVICE || 'docufy-web');
  const version = import.meta.env.VITE_APP_RELEASE as string | undefined;
  const site = ddSite();
  const env = ddEnv();
  const sessionSampleRate = Number(import.meta.env.VITE_DD_SESSION_SAMPLE_RATE ?? 100);
  const sessionReplaySampleRate = Number(import.meta.env.VITE_DD_SESSION_REPLAY_SAMPLE_RATE ?? 100);

  try {
    datadogRum.init({
      applicationId,
      clientToken,
      site,
      service,
      env,
      version,
      sessionSampleRate: Number.isFinite(sessionSampleRate) ? sessionSampleRate : 100,
      sessionReplaySampleRate: Number.isFinite(sessionReplaySampleRate)
        ? sessionReplaySampleRate
        : 100,
      trackResources: true,
      trackLongTasks: true,
      trackUserInteractions: true,
      defaultPrivacyLevel: 'mask-user-input',
      beforeSend: (event) => shouldSendRumEvent(event),
      allowedTracingUrls: [
        {
          match: /https?:\/\/([^/]*\.)?docufyai\.com/i,
          propagatorTypes: ['datadog', 'tracecontext'],
        },
        { match: /\/api\//, propagatorTypes: ['datadog', 'tracecontext'] },
      ],
      // Route template names need createBrowserRouter from
      // @datadog/browser-rum-react/react-router-v7; until then, default URL views apply.
      plugins: [reactPlugin()],
    });

    datadogLogs.init({
      clientToken,
      site,
      service,
      env,
      version,
      forwardErrorsToLogs: true,
      forwardConsoleLogs: ['error', 'warn'],
      sessionSampleRate: Number.isFinite(sessionSampleRate) ? sessionSampleRate : 100,
    });

    datadogRum.startSessionReplayRecording();
    initialized = true;
  } catch (err) {
    console.warn('Datadog initialization failed; app will run without Datadog RUM', err);
  }
}

/** Datadog transport for boundary context — prefer `reportErrorFromBoundary` in UI. */
export function captureErrorToDatadog(error: Error, context: ErrorBoundaryReportContext): void {
  if (!initialized) {
    return;
  }
  datadogRum.addError(error, {
    boundary: context.boundary,
    route: context.routeName,
    feature: context.featureName,
    errorId: context.errorId,
    componentStack: context.componentStack,
  });
  datadogLogs.logger.error(
    error.message,
    {
      boundary: context.boundary,
      route: context.routeName,
      feature: context.featureName,
      errorId: context.errorId,
    },
    error,
  );
}

export function isDatadogInitialized(): boolean {
  return initialized;
}

type ChatFlowLogLevel = 'info' | 'warn' | 'error';

/**
 * Emit privacy-safe chat lifecycle diagnostics from deployed builds.
 * Callers must provide metadata only (counts/flags/ids), never extracted document values.
 */
export function logChatFlow(
  event: string,
  context: Record<string, unknown>,
  level: ChatFlowLogLevel = 'info',
): void {
  if (!initialized) {
    return;
  }

  try {
    datadogLogs.logger[level](`[chat-flow] ${event}`, {
      ...context,
      chat_flow_event: event,
    });
  } catch {
    // Diagnostics are best-effort and must never interrupt the chat flow.
  }
}

/** Attach authenticated user to RUM/Logs sessions (id only; email domain optional). */
export function setDatadogUser(user: {
  id: string;
  email?: string | null;
  name?: string | null;
  organizationId?: string | null;
}): void {
  if (!initialized) {
    return;
  }
  const emailDomain =
    user.email && user.email.includes('@') ? user.email.split('@').pop()?.toLowerCase() : undefined;
  datadogRum.setUser({
    id: user.id,
    name: user.name || undefined,
    email: undefined, // privacy: do not send raw email
    organization_id: user.organizationId || undefined,
    email_domain: emailDomain,
  });
  datadogLogs.setUser({
    id: user.id,
    name: user.name || undefined,
    organization_id: user.organizationId || undefined,
    email_domain: emailDomain,
  });
}

export function clearDatadogUser(): void {
  if (!initialized) {
    return;
  }
  datadogRum.clearUser();
  datadogLogs.clearUser();
}
